import { Context } from "../models/context";
import { Data } from "../models/data";
import { Util } from "../utils/util";
import { IRegionalizedBanner, IRegionZonePoint, BannerSizeCode, RegionRuleType } from "./banner-service.interface";
import { ProfileService } from "./profile-service";

export class BannerService {
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _profileService: ProfileService;

    constructor(data: Data, profileService: ProfileService, context: Context) {
        this._data = data;
        this._context = context;
        this._profileService = profileService;
    }

    /**
     * Gets applicable banners from brand data cache
     * Filters by region (client-side), device type, schedule, and coupon validity.
     */
    public async getApplicableBannersAsync(
        brandStoreKey: string,
        userLatitude: number | null,
        userLongitude: number | null
    ): Promise<IRegionalizedBanner[]> {
        const allBanners = this._data.getBrandProperty("OnlineBanners") || [];

        // Filter by region based on whether user location is available
        const regionFilteredBanners = userLatitude !== null && userLongitude !== null
            ? this._filterBannersByRegion(allBanners, userLatitude, userLongitude)
            : this._filterBannersWithoutLocation(allBanners);

        // Filter by device type (app vs browser)
        const isApp = Util.isAppView();
        const deviceFilter = isApp ? 'showOnApp' : 'showOnBrowser';
        const deviceFilteredBanners = regionFilteredBanners.filter(b => b[deviceFilter]);

        // Filter by schedule
        const scheduleFilteredBanners: IRegionalizedBanner[] = [];
        for (const banner of deviceFilteredBanners) {
            if (await this._isScheduleActive(banner)) {
                scheduleFilteredBanners.push(banner);
            }
        }

        return scheduleFilteredBanners;
    }

    /**
     * Filters banners for non-geolocated users (no location data available).
     */
    private _filterBannersWithoutLocation(banners: IRegionalizedBanner[]): IRegionalizedBanner[] {
        return banners.filter(banner => banner.regionRuleType === RegionRuleType.ALL);
    }

    /**
     * Filters banners by region rules for geolocated users.
     */
    private _filterBannersByRegion(
        banners: IRegionalizedBanner[],
        latitude: number,
        longitude: number
    ): IRegionalizedBanner[] {
        return banners.filter(banner => {
            // ALL: Always show (non-regionalized)
            if (banner.regionRuleType === RegionRuleType.ALL) {
                return true;
            }

            // No regions defined
            if (!banner.regions || banner.regions.length === 0) {
                // EXCLUDE with no regions = show everywhere (non-regionalized behavior)
                // INCLUDE with no regions = don't show anywhere (nothing to whitelist)
                return banner.regionRuleType === RegionRuleType.EXCLUDE;
            }

            // Check if user is in any of the defined regions
            const isInAnyRegion = banner.regions.some(region => 
                this._isPointInPolygon(region.points, latitude, longitude)
            );

            // Apply rule based on type
            return banner.regionRuleType === RegionRuleType.INCLUDE
                ? isInAnyRegion      // INCLUDE: show only if user IS in a region
                : !isInAnyRegion;    // EXCLUDE: show only if user is NOT in any region
        });
    }

    private _isPointInPolygon(points: IRegionZonePoint[], latitude: number, longitude: number): boolean {
        if (!points || points.length < 3) return false;

        const point = { Latitude: latitude, Longitude: longitude };

        return Util.isPointInPolygon(points, point);
    }

    /**
     * Gets the appropriate banner image URL for current screen size.
     * Constructs full URL from image path.
     */
    public getBannerImageUrlForScreenSize(banner: IRegionalizedBanner): string | null {
        const width = window.innerWidth;
        let sizeCode: BannerSizeCode;

        if (width >= 1200) {
            sizeCode = BannerSizeCode.LARGE;
        } else if (width >= 768) {
            sizeCode = BannerSizeCode.MEDIUM;
        } else {
            sizeCode = BannerSizeCode.SMALL;
        }

        const image = banner.images.find(img => img.sizeCode === sizeCode);
        if (!image?.imagePath) {
            return null;
        }

        return this._constructImageUrl(image.imagePath);
    }

    private _constructImageUrl(imagePath: string): string {
        const contentFolderUrl = this._context.getProperty("publicStorageUrl");
        return `${contentFolderUrl}${imagePath}`;
    }

    private async _isScheduleActive(banner: IRegionalizedBanner): Promise<boolean> {
        if (!banner.timeSensitive || !banner.schedules || banner.schedules.length === 0) {
            return true;
        }

        const { timeOffset, businessStartMinute } = await this._getStoreTimeSettings();

        const storeNow = Util.NowStore(timeOffset);
        const businessDate = Util.GetBUSDate(storeNow, timeOffset, businessStartMinute);
        const currentDay = businessDate.getDay();

        let currentMinute = storeNow.getHours() * 60 + storeNow.getMinutes();

        if (currentMinute < businessStartMinute) {
            currentMinute += 1440;
        }

        const todaySchedule = banner.schedules.find(s => s.weekDayNo === currentDay && s.isActive);
        
        if (!todaySchedule) {
            return false;
        }

        return currentMinute >= todaySchedule.startMinute && currentMinute < todaySchedule.endMinute;
    }

    /**
     * Gets timezone and business start minute for schedule calculations.
     * Priority: 1) Selected store, 2) User's default store, 3) Nearest store for order type, 4) Brand defaults
     */
    private async _getStoreTimeSettings(): Promise<{ timeOffset: number; businessStartMinute: number }> {
        const selectedStoreSettings = this._getSelectedStoreSettings();
        if (selectedStoreSettings) {
            return selectedStoreSettings;
        }

        const defaultStoreSettings = await this._getDefaultStoreSettingsAsync();
        if (defaultStoreSettings) {
            return defaultStoreSettings;
        }

        const nearestStoreSettings = await this._getNearestStoreSettingsAsync();
        if (nearestStoreSettings) {
            return nearestStoreSettings;
        }

        return this._getBrandDefaultSettings();
    }

    private _getSelectedStoreSettings(): { timeOffset: number; businessStartMinute: number } | null {
        const currentStoreInfo = this._data.getProperty("StoreInfo");
        if (!currentStoreInfo?.storeKey) {
            return null;
        }

        const timeOffset = this._data.getProperty("TimeOffset");
        const businessStartMinute = this._data.getProperty("Settings")?.BSM;

        if (timeOffset === undefined || businessStartMinute === undefined) {
            return null;
        }

        return { timeOffset, businessStartMinute };
    }

    private async _getDefaultStoreSettingsAsync(): Promise<{ timeOffset: number; businessStartMinute: number } | null> {
        if (!this._profileService.user.isLoggedIn()) {
            return null;
        }

        const defaultStore = this._profileService.user.getProperty("DefaultStore");
        if (!defaultStore?.StoreKey) {
            return null;
        }

        const storeLocation = this._data.locations?.find(loc => loc.storeKey === defaultStore.StoreKey);
        if (!storeLocation) {
            return null;
        }

        const timeOffset = await this._data.loadStoreTimeOffsetAsync(defaultStore.StoreKey);
        return {
            timeOffset,
            businessStartMinute: storeLocation.businessStartMinute ?? 240
        };
    }

    private async _getNearestStoreSettingsAsync(): Promise<{ timeOffset: number; businessStartMinute: number } | null> {
        const nearestStore = this._getNearestStoreForOrderType();
        if (!nearestStore) {
            return null;
        }

        const timeOffset = await this._data.loadStoreTimeOffsetAsync(nearestStore.storeKey);
        return {
            timeOffset,
            businessStartMinute: nearestStore.businessStartMinute ?? 240
        };
    }

    private _getBrandDefaultSettings(): { timeOffset: number; businessStartMinute: number } {
        const timeOffset = this._data.getBrandProperty("TimeOffset") ?? 0;
        const businessStartMinute = this._data.getBrandProperty("Settings")?.BSM ?? 240;

        return { timeOffset, businessStartMinute };
    }

    /**
     * Gets the nearest store that supports the current order type
     */
    private _getNearestStoreForOrderType() {
        const locations = this._data.locations;
        if (!locations || locations.length === 0) {
            return null;
        }

        // Get current order type (default to TAKE_OUT if not set)
        const orderTypeId = this._context.getProperty("defaultOrderType") ?? 2;

        const matchingStores = locations.filter(store => 
            store.orderTypes?.includes(orderTypeId)
        );

        if (matchingStores.length === 0) {
            return null;
        }

        // If stores have distance calculated, find the closest one
        const storesWithDistance = matchingStores.filter(s => s.distance !== undefined);
        if (storesWithDistance.length > 0) {
            let closestStore = storesWithDistance[0];
            let shortestDistance = closestStore.distance ?? Infinity;

            for (const store of storesWithDistance) {
                const currentDistance = store.distance ?? Infinity;
                if (currentDistance < shortestDistance) {
                    closestStore = store;
                    shortestDistance = currentDistance;
                }
            }

            return closestStore;
        }

        // Otherwise, return the first matching store
        return matchingStores[0];
    }
}